// @ts-check
import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import store from '../store';
import { LiveWorker } from '../workers';
// Models
import LiveStatisticsParamsModel from '../models/signalr/live-statistics-params-model';
import LiveMatchDetailsParamsModel from '../models/signalr/live-match-details-params-model';
import { hubs, hubsProxyMethods } from '../models/signalr-core';
import LiveHubModel from '../models/signalr/live-hub-model';
import BetslipPlacementParamsModel from '../models/signalr/betslip-placement-params-model';
// Services
import NegotiationsService from './mybets/negotiations-service';
import CashoutService from './mybets/cashout-request-service';
import { EventBus } from './event-bus-service';
import AutoPrintService from '../agent/services/print/Auto-print-service';
import LocalStorageService from './storage/local-storage-service';

import { setToastNotification, userBetslipsStore } from '@bto-services/toast-notification';
import { getBetNotificationStatus, getBetToastAdditionalTime } from 'bto-models/toast-notification';
import { BETSLIP_MSG_STATUS } from 'bto-models/betslip';
import { betslipStore, getFreebets, changeItemOdd, quickBetKeyStore } from '@bto-services/betslip-core';
import ConfigService from '@bto-services/api/shared/config';
import { updateAspireBalance, addGTMEventOnPlace } from '@bto-services/external';
import { topEventsStore, updateLiveTopEvent } from '@bto-services/top-events';
import { combinedSliderStore, updateCSLiveTopEvent } from '@bto-services/combined-slider';

/**
 * @typedef {import("@microsoft/signalr").HubConnection} HubConnection
 * @typedef {import("../main").default} MainApp
 * @typedef {import('bto-models/signalr/interfaces').ISignalrRequest} ISignalrRequest
 */

/**
 * @implements ISignalrRequest
 */
export default class SignalrCoreService {
  static onConnectionStartMethods = {
    [hubs.liveDetailsHub]: 'initLiveMatchDetails',
    [hubs.liveStatisticsHub]: 'initLiveStatisticDetails',
    [hubs.notificationHub]: 'initNotifications',
    [hubs.cashoutHub]: 'initCashout',
    [hubs.liveHub]: 'initLiveHub',
  }

  /**
   * Signalr Core Service consturctor
   * @param {MainApp} vueApp - main app
   */
  constructor(vueApp) {
    // send all app settings in worker for further usage
    LiveWorker.postMessage({ ev: 'settings', resp: APP_SETTINGS });
    LiveWorker.addEventListener('message', (msg) => {
      if (msg.data.type === 'betslipUpdate') {
        msg.data.data.selectedBetItems.forEach(si => {
          changeItemOdd(si.item, si.newOdd, si.isBetStop);
        });
      }
      if (msg.data.type === 'liveTopEventsUpdate') {
        msg.data.data.liveTopEvents.forEach(lte => {
          updateLiveTopEvent(lte);
        });
      }
      if (msg.data.type === 'liveCombinedSliderUpdate') {
        msg.data.data.liveCombinedSliderItems.forEach(lcsi => {
          updateCSLiveTopEvent(lcsi);
        });
      }
    });
    /**
     * Main Vue app
     */
    this.mainApp = vueApp;

    /**
     * SignalR base url
     * @type {string}
     */
    this.url = APP_SETTINGS.path.signalrUrl || `${APP_SETTINGS.path.apiUrl}signalr`;

    /**
     * flag used to indicate if we should start liveHub connection proxy on live hub connect
     * @type {boolean}
     */
    this.startLiveHubOnStart = true;
    /**
     * betradar id for live match details needed for reconnect
     * @type {string|number|null}
     */
    this.liveDetailsBetradarId = null;
    /**
     * betradar id for live match statistics needed for reconnect
    //  * @type {string|number|null}
     */
    this.liveStatisticsBetradarId = null;

    /**
     * @type {{[hubName:string]: HubConnection}}
     */
    this.connections = {};
    const hubConnections = Object.values(hubs);
    /**
     * Create hub connections
     */
    hubConnections.forEach((c) => {
      const opts = {
        headers: {
          TenantID: APP_SETTINGS.tenant,
        },
        transport: 1,
        skipNegotiation: true,
      };
      const connection = new HubConnectionBuilder()
        .withUrl(`${this.url}/${c}`, opts)
        .withAutomaticReconnect()
        .configureLogging(LogLevel.None)
        .build();
      this.connections[c] = connection;
    });
    /**
     * Add each hub connection event listeners before starting connection
     */
    this.addLiveHubListeners();
    this.addLiveMatchDetailsListeners();
    this.addLiveStatisticsListeners();
    this.addNotificationListeners();
    this.addCashOutListeners();

    this.startHubs();
  }

  /**
   * Start connection to each hub,
   * if inital method is present execute it after hub has successfully conected
   */
  startHubs() {
    const hubConnections = Object.values(hubs);

    hubConnections.forEach((hubName) => {
      const c = this.connections[hubName];
      // Seconds to try to connect to hub if inital start failed
      let reconnectSteps = [30, 10, 5, 2];
      /**
       * Hub proxy methods to call after we connect/reconnect to hub
       */
      const onConnected = () => {
        const initMethodName = SignalrCoreService.onConnectionStartMethods[hubName];
        // Check if we have the function specified
        if (this[initMethodName] && typeof this[initMethodName] === 'function') {
          this[initMethodName]();
        }
      };
      // Start hub connection callback
      const start = () => {
        const rs = reconnectSteps.pop();
        // If we failed to connect after all reconnectSteps specifed return
        if (!rs) {
          return;
        }
        c.start().then(onConnected).catch((err) => {
          setTimeout(start, 1000 * rs);
          // eslint-disable-next-line no-console
          console.log(err);
        });
      };

      // Start hub connection
      start();
      // Add on reconnected callback to hub
      c.onreconnected(onConnected);
    });
  }

  /**
   * Recconect to signalR hub,
   * we only disocnnect for now because we autoreconnect when connection is lost
   * this is done on login/logut to invoke correct hub methods
   */
  reconnect() {
    const hubConnections = Object.values(hubs);
    hubConnections.forEach((hubName) => {
      this.connections[hubName].stop().then(() => {
        if (hubConnections.every(hn => this.connections[hn].state === HubConnectionState.Disconnected)) {
          this.startHubs();
        }
      });
    });
  }

  /**
   * Connect to live match details hub proxy if we are on live match details route during connection start
   */
  initLiveMatchDetails() {
    const route = this.mainApp.$route;
    const agentRoute = process.env.VUE_APP_IS_AGENT && route.params.matchesType === 'live';
    const webRoute = route.name === 'seoGameLive';
    if (route.params.matchId && (agentRoute || webRoute)) {
      this.connectToLiveMatchDetailsHub(route.params.matchId);
      return;
    }
    if (this.liveDetailsBetradarId) {
      this.connectToLiveMatchDetailsHub(this.liveDetailsBetradarId);
    }
  }
  /**
   * Connect to live statistic hub
   */
  initLiveStatisticDetails() {
    const route = this.mainApp.$route;
    const webRoute = route.name === 'seoGameLive';

    if (route.params.matchId && webRoute) {
      this.connectToLiveStatisticHub(route.params.matchId);
      return;
    }
    if (this.liveStatisticsBetradarId) {
      this.connectToLiveStatisticHub(this.liveStatisticsBetradarId);
    }
  }

  /**
   * Connect to notification hub proxy if player is present and logged in
   * during connection start
   */
  initNotifications() {
    if (store.getters.isUserLogged) {
      this.connectToNotificationHub();
    }
  }

  /**
   * Connect to cashout hub proxy if player is present and logged in
   * and cashout is enabled in DB settings for client
   * during connection start
   */
  initCashout() {
    if (!process.env.VUE_APP_IS_AGENT && store.getters.isUserLogged && APP_SETTINGS.settings.useCashout) {
      this.connectToCashoutHub();
    }
  }

  /**
   * Connect to live hub proxy if startLiveHub is set
   */
  initLiveHub() {
    if (this.startLiveHubOnStart) {
      this.connectToLiveHub();
    }
  }

  /**
   * Connect to live match details hub proxy
   * @param {string|number} betRadarID - betRadarID
   */
  connectToLiveMatchDetailsHub(betRadarID) {
    this.liveDetailsBetradarId = betRadarID;
    const liveMatchDetailsParamsModel = new LiveMatchDetailsParamsModel({ betRadarID });
    const hubName = hubs.liveDetailsHub;
    this.connections[hubName]
      .invoke(hubsProxyMethods[hubName].connectMatchDetails, ...liveMatchDetailsParamsModel.loadParams());
  }
  /**
   * Connect to live statistic hub proxy
   */
  connectToLiveStatisticHub(betRadarID) {
    this.liveStatisticsBetradarId = betRadarID;
    const liveStatisticsParamsModel = new LiveStatisticsParamsModel({ betRadarID });
    const hubName = hubs.liveStatisticsHub;
    this.connections[hubName]
      .invoke(hubsProxyMethods[hubName].connect, ...liveStatisticsParamsModel.loadParams());
  }
  /**
   * Disconnect from live match details hub proxy
   */
  disconnectFromLiveMatchDetailsHub() {
    this.liveDetailsBetradarId = null;
    const hubName = hubs.liveDetailsHub;
    this.connections[hubName].invoke(hubsProxyMethods[hubName].disconnect, APP_SETTINGS.tenant);
  }

  /**
   * Disconnect from live statistic hub proxy
   */
  disconnectFromLiveStatisticHub() {
    this.liveStatisticsBetradarId = null;
    const hubName = hubs.liveStatisticsHub;
    this.connections[hubName].invoke(hubsProxyMethods[hubName].disconnect, APP_SETTINGS.tenant);
  }

  /**
   * Connect to live hub proxy
   */
  connectToLiveHub() {
    const hubName = hubs.liveHub;
    this.startLiveHubOnStart = true;
    if (!this.checkHubConnection(hubName)) {
      return;
    }
    const liveHubModel = new LiveHubModel({});
    this.connections[hubName].invoke(hubsProxyMethods[hubName].connect, ...liveHubModel.loadParams());
  }

  /**
   * Disconnect from live hub proxy
   */
  disconnectFromLiveHub() {
    const hubName = hubs.liveHub;
    this.startLiveHubOnStart = false;
    const liveHubModel = new LiveHubModel({});
    this.connections[hubName].invoke(hubsProxyMethods[hubName].disconnect, ...liveHubModel.loadParams());
  }

  /**
   * Connect to notification hub proxy
   */
  connectToNotificationHub() {
    const betslipPlacementParamsModel = new BetslipPlacementParamsModel({});
    const hubName = hubs.notificationHub;
    const notificationHub = this.connections[hubName];
    notificationHub.invoke(hubsProxyMethods[hubName].connect, ...betslipPlacementParamsModel.loadParams());
  }

  /**
   * Connect to cashout hub proxy
   */
  connectToCashoutHub() {
    const hubName = hubs.cashoutHub;
    const langId = window.btoLangId || 1;
    /**
     * params: [SB-userID, External-userID, tenant, languageID, mobile]
     */
    const cashoutHubParamsModel = [0, APP_SETTINGS.player.id, APP_SETTINGS.tenant, langId, false];
    this.connections[hubName].invoke(hubsProxyMethods[hubName].connect, ...cashoutHubParamsModel);
  }

  /**
   * Connect to cashout start calc used in cashout V2 for optimization
   */
  connectToCashoutHubStartCalc() {
    const hubName = hubs.cashoutHub;
    /**
     * params: [SB-userID, External-userID, tenant,]
     */
    const cashoutHubParamsModel = [0, APP_SETTINGS.player.id, APP_SETTINGS.tenant];
    this.connections[hubName].invoke(hubsProxyMethods[hubName].startCalculation, ...cashoutHubParamsModel);
  }
  
  /**
   * Disconect to cashout start calc used in cashout V2 for optimization
   */
  disconnectFromCashoutHubStopCalc() {
    const hubName = hubs.cashoutHub;
    /**
     * params: [SB-userID, External-userID, tenant,]
     */
    const cashoutHubParamsModel = [0, APP_SETTINGS.player.id, APP_SETTINGS.tenant];
    this.connections[hubName].invoke(hubsProxyMethods[hubName].stopCalculation, ...cashoutHubParamsModel);

  }
  /**
   * Add listeners to live hub
   */
  addLiveHubListeners() {
    // Live matches
    this.connections[hubs.liveHub].on('availableLiveMatches', (resp) => {
      const data = this.decompressData(resp);
      window.btoSignalRAvailibility = data;
      LiveWorker.postMessage({ ev: 'availibility', resp: data, items: betslipStore.items, topEventsMap: topEventsStore.value.liveTopEventsMap, combinedSliderItemsMap: combinedSliderStore.value.liveTopEventsMap });
      EventBus.$emit('newLiveMatch');
    });
    // Live matches odds update event
    this.connections[hubs.liveHub].on('oddsChange', (resp) => {
      const data = this.decompressData(resp);
      window.btoSignalOddChange = data;
      LiveWorker.postMessage({ ev: 'oddschange', resp: data, items: betslipStore.items, topEventsMap: topEventsStore.value.liveTopEventsMap, combinedSliderItemsMap: combinedSliderStore.value.liveTopEventsMap });
    });
  }

  /**
   * Add listeners to live match details hub
   */
  addLiveMatchDetailsListeners() {
    this.connections[hubs.liveDetailsHub].on('liveChangedOdds', (resp) => {
      const data = this.decompressData(resp);
      window.btoSignalRLiveChangedOdds = data;
      EventBus.$emit('newLiveMatchDetails', { data, items: betslipStore.items });
      store.commit('setBsportsTrackerData', { data });
    });
    this.connections[hubs.liveDetailsHub].on('playerPropsDetails', (resp) => {
      const data = this.decompressData(resp);
      window.btoLivePlayerMarkets = data;
      EventBus.$emit('livePlayerMarkets', data);
    });
  }

  /**
   * Add listeners to live match statistics hub
   */
  addLiveStatisticsListeners() {
    this.connections[hubs.liveStatisticsHub].on('liveMatchStatistics', (resp) => {
      const data = this.decompressData(resp);
      window.btoSignalRLiveStatistics = data;
      store.commit('setBsportsStatisticsData', { data });
    });
  }

  /**
   * Add listeners to notification hub
   */
  addNotificationListeners() {
    const hubName = hubs.notificationHub;
    LocalStorageService.set('notifications', []);

    // Listen to betslip placement messages
    this.connections[hubName].on('betSlipPlacement', (resp, betslipgroup) => {
      const regularKey = `${APP_SETTINGS.player.id}_${betslipgroup.id}`;
      const betNotificationStatus = getBetNotificationStatus(resp);
      const betToastAdditionalTime = getBetToastAdditionalTime(betNotificationStatus);
      const expireTime = Date.now() + betToastAdditionalTime;
      const itemKeyQuick = quickBetKeyStore[regularKey];
      const quickbetKey = itemKeyQuick ? `${APP_SETTINGS.player.id}_${itemKeyQuick}` : '';
      const key = quickbetKey || regularKey;

      setToastNotification(key, {
        playerId: APP_SETTINGS.player.id,
        expireTime,
        message: resp.ResponseMessage,
        status: 1,
        keepBetslip: betNotificationStatus === BETSLIP_MSG_STATUS.PENDING,
        betslip: {
          status: betNotificationStatus,
          response: resp,
          betslipGroupId: betslipgroup.bsgid,
        }
      });
      if (betNotificationStatus === BETSLIP_MSG_STATUS.PLACED) {
        updateAspireBalance();
        // If freebet was awarded or we used freebet fetch freebets
        if (resp.FreeBetAward || userBetslipsStore[key] && userBetslipsStore[key].freebetId) {
          setTimeout(() => {
            getFreebets();
          }, 2000);
        }
        addGTMEventOnPlace(betslipgroup.bsgid, userBetslipsStore[key]);
      }
    });

    // Listen for connectionId so we can set the notification hub connectionId
    this.connections[hubName].on('ConnectionId', (connectionId) => {
      ConfigService.customHeaders.connectionId = connectionId;
    });

    // Listen to negotiations messages
    this.connections[hubName].on('negotiateBetslips', (resp) => {
      NegotiationsService.processNegotiations(resp);
    });

    // Listen to cashout notifications messages
    this.connections[hubName].on('cashOut', (resp) => {
      CashoutService.processCashoutMsg(resp);
      EventBus.$emit('cashedOutMsg', resp);
    });

    // Listen to add notification messages
    // TODO: refactor the code for notifications
    // this.connections[hubName].on('addNotification', (_resp) => {
    //   return;
    // });

    // Agent specific notification listeners
    if (process.env.VUE_APP_IS_AGENT) {
      // Listen to print ticket messages
      this.connections[hubName].on('printTicket', (betslipgroupId) => {
        AutoPrintService.startAutoPrint({}, betslipgroupId);
      });
    }

    this.connections[hubName].on('userHasFreeBets', () => {
      setTimeout(() => {
        getFreebets();
      }, 2000);
    });
  }

  /**
   * Add listeners to cashout hub
   */
  addCashOutListeners() {
    this.connections[hubs.cashoutHub].on('myBets', (resp) => {
      store.commit('addMyBets', resp);
      CashoutService.processCashout(resp);
    });
  }

  /**
   * Decompress gzipped data from signalR
   * @param resp
   * @returns {any}
   */
  decompressData(resp) {
    const gzipedStr = atob(resp);
    const ungziped = pako.ungzip(gzipedStr, { to: 'string' });
    return JSON.parse(ungziped.replace(/\0/g, ''));
  }

  /**
   * Check if hub is connected
   * @param {string} hubName - Name of hub
   */
  checkHubConnection(hubName) {
    return this.connections[hubName] && this.connections[hubName].state === HubConnectionState.Connected;
  }

  /**
   * Returns the text from a HTML string
   * @param {string} html - The html string
   */
  stripHtml(html) {
    const aTag = document.createElement('a');
    aTag.innerHTML = html;
    return aTag.textContent || aTag.innerText || '';
  }
}
